import { Injectable } from '@angular/core';
import {
  addDoc, collection, deleteDoc, doc, DocumentReference, Firestore, runTransaction, setDoc, Transaction, updateDoc,
} from '@angular/fire/firestore';

import { LogService, LogZone } from 'app/core/logging';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { toDotNotation } from '../utils';

export enum ApiState {
  Processing,
  Success,
  Failure,
}

@Injectable()
export class PersistenceService {
  public state = new BehaviorSubject<Map<string, ApiState>>(new Map<string, ApiState>());

  public constructor(
    private afs: Firestore,
    private logService: LogService,
  ) {
    this.logService = logService.for(LogZone.FIRESTORE);
  }

  public runTransaction(fn: (transaction: Transaction) => Promise<unknown>, key: string = null): Promise<void> {
    this.updateState(key, ApiState.Processing);
    this.logService.debug('Running transaction', { fn });
    const request = runTransaction(this.afs, fn);
    return this.watch(key, request);
  }

  public getDoc(docPath: string): DocumentReference {
    this.logService.debug('Get doc', { docRef: docPath });
    return doc(this.afs, docPath);
  }

  public add<Tm extends unknown>(path: string, item: Tm, key: string = null): Promise<DocumentReference> {
    this.updateState(key, ApiState.Processing);
    this.logService.debug('Add', { path, item });
    const request = addDoc(collection(this.afs, path), item);
    return this.watch(key, request);
  }

  public set<Tm extends unknown>(path: string, item: Tm, key: string = null): Promise<void> {
    this.updateState(key, ApiState.Processing);
    this.logService.debug('Set', { path, item });
    const request = setDoc(doc(this.afs, path), item);
    return this.watch(key, request);
  }

  public patch<Tm extends unknown>(path: string, item: Partial<Tm>, key: string = null): Promise<void> {
    const update = toDotNotation(item);
    this.updateState(key, ApiState.Processing);
    this.logService.debug('Update', { path, item: update });
    const request = updateDoc(doc(this.afs, path), update);
    return this.watch(key, request);
  }

  public remove(path: string): Promise<void> {
    this.logService.debug('Remove', path);
    const request = deleteDoc(doc(this.afs, path));
    return this.watch(path, request);
  }

  public getState(key: string): Observable<ApiState> {
    return this.state.pipe(
      map((state) => state[key]),
      distinctUntilChanged(),
    );
  }

  private updateState(key: string, value: ApiState): void {
    if (!key) {
      return;
    }
    // avoid modifying the map by reference bc it may be referenced
    const state = new Map(this.state.value);
    state[key] = value;
    this.state.next(state);
  }

  private watch<T>(key: string, request: Promise<any>): Promise<T> {
    return request
      .then((x) => {
        this.updateState(key, ApiState.Success);
        return x;
      })
      .catch((x) => {
        this.logService.error(x);
        this.updateState(key, ApiState.Failure);
      });
  }
}
